<svelte:options runes={false} />
<script>
  // Per-parent TWAP execution report (TASK-20260823-twap-strategies-tca AC 25): hero KPI tiles,
  // savings waterfall, execution tape, pace chart, child blotter, market footprint, narrative.
  // Live mode polls the authoritative read every 5 s while the parent is Working (PR-C adds SSE);
  // finished parents render once. The paper/live fee source is shown on every scorecard so a paper
  // fee schedule is never read as live savings. Selection is acknowledged before the first await
  // (lesson 2026-07-28): the caller's summary row renders behind a skeleton until the detail lands.
  import { onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import {
    kpis, qualityTiles, savingsWaterfall, paceSeries, tapeSeries, narrative, statusTone, isWorking, feeSourceBadge,
    STATUS_LABELS, fmtQty,
  } from '../../../lib/twapReport.js';
  import { heatmapGrid } from '../../../lib/twapTab.js';
  import { describePolicy, requestToPolicy, POSTURE_NAMES } from '../../../lib/twapPolicy.js';
  import { formatQuoteAmount } from '../../../lib/quoteCurrency.js';
  import { targetOf, isRealMoney, targetBadge } from '../../../lib/runContext.js';
  import SavingsWaterfall from './SavingsWaterfall.svelte';
  import ExecutionTape from './ExecutionTape.svelte';
  import PaceChart from './PaceChart.svelte';
  import ChildBlotter from './ChildBlotter.svelte';
  import { blockedCycleWarning } from '../../../lib/twapRejection.js';
  import MarketFootprint from './MarketFootprint.svelte';
  import NarrativeStrip from './NarrativeStrip.svelte';
  import LatencyTile from './LatencyTile.svelte';
  import MakerCaptureHeatmap from './MakerCaptureHeatmap.svelte';

  export let parentId = null;
  export let orgId = null;
  /** Optional TwapParentSummary already on hand (the blotter row) — rendered until the detail arrives. */
  export let initial = null;
  export let pollMs = 5000;

  let detail = null;
  let error = null;
  let loading = false;
  let timer = null;
  let requestSeq = 0;
  let loadedFor = null;

  async function load() {
    if (!parentId) return;
    const seq = ++requestSeq;
    const forId = parentId;
    loading = detail === null;
    try {
      const d = await api.getTwapParent(forId, orgId);
      if (seq !== requestSeq) return; // a newer selection superseded this response
      detail = d;
      error = d ? null : 'This parent is no longer available.';
      loadedFor = forId;
    } catch (e) {
      if (seq !== requestSeq) return;
      error = /404/.test(e?.message ?? '') ? 'Parent not found (or not in this organisation).' : (e?.message ?? 'Failed to load the report');
    } finally {
      if (seq === requestSeq) loading = false;
    }
  }

  function stopPolling() {
    if (timer) { clearInterval(timer); timer = null; }
  }

  // A new parent id: acknowledge synchronously (drop the old detail so the summary/skeleton shows),
  // then fetch. Polling is (re)armed from the loaded status below.
  $: if (parentId !== loadedFor) {
    detail = null;
    error = null;
    stopPolling();
    load();
    loadHeatmap(parentId);
  }

  // PR-C2: the per-parent maker-capture heatmap (GET /api/twap/parents/{id}/heatmap) — its own read,
  // its own states, and the same selection guard so a slow response never lands on a newer parent.
  let heatmap = null;
  let heatmapLoading = false;
  let heatmapError = null;
  let heatmapSeq = 0;
  async function loadHeatmap(forId) {
    heatmap = null;
    heatmapError = null;
    if (!forId) return;
    const seq = ++heatmapSeq;
    heatmapLoading = true;
    try {
      const res = await api.getTwapParentHeatmap(forId, orgId);
      if (seq !== heatmapSeq) return;
      heatmap = heatmapGrid(res);
    } catch (e) {
      if (seq !== heatmapSeq) return;
      heatmapError = e?.message ?? 'Failed to load the heatmap';
    } finally {
      if (seq === heatmapSeq) heatmapLoading = false;
    }
  }

  $: parent = detail?.parent ?? initial ?? null;
  $: working = isWorking(parent?.status);
  $: if (detail && working && !timer && pollMs > 0) {
    timer = setInterval(load, pollMs);
  } else if (detail && !working && timer) {
    stopPolling();
  }
  onDestroy(stopPolling);

  $: scorecard = detail?.scorecard ?? null;
  $: children = detail?.children ?? [];
  // AC 9: a cycle rejected twice or more is STUCK, not slow — say so at the parent, so the desk does
  // not have to open the blotter to find out why a parent has stopped progressing.
  $: blocked = blockedCycleWarning(children);
  $: currency = parent?.quoteCurrency ?? 'USD';
  $: tiles = kpis(parent, scorecard);
  $: quality = qualityTiles(scorecard);
  $: waterfall = scorecard ? savingsWaterfall(scorecard) : null;
  $: pace = paceSeries(children, parent);
  $: tape = tapeSeries(detail?.tape, children, parent, scorecard);
  $: story = narrative(parent, scorecard);
  $: fee = feeSourceBadge(scorecard?.feeRateSource ?? parent?.feeRateSource);
  // ADR-0040: the parent's run target is `exchgId` (venue) + `mode` (contract: TwapReadModels.cs). Simulated
  // = anything that is not real money (Paper on any venue, or Live on the sandbox). The badge fails closed —
  // UNKNOWN, never LIVE — for a parent whose target cannot be classified.
  $: parentTarget = targetOf(parent);
  $: paperVenue = parent ? !isRealMoney(parentTarget) : false;
  $: modeBadge = targetBadge(parentTarget);
  $: policyText = detail?.policy
    ? describePolicy(requestToPolicy({
        enabled: true,
        durationMinutes: parent?.durationSeconds ? Math.round(parent.durationSeconds / 60) : null,
        sliceIntervalSeconds: detail.policy.scheduleFrequency,
        slippageCapPct: detail.policy.slippageThreshold,
        priceBasis: String(detail.policy.limitPriceBasedOn ?? 'Mid').replace('MidPrice', 'Mid'),
        maxBookDepth: detail.policy.maxOrderBookDepth,
        doNotTradeAheadOfSchedule: detail.policy.doNotTradeAheadOfSchedule,
        pricingStrategySequence: detail.policy.pricingStrategySequence,
      }))
    : null;
  $: ladderText = (detail?.policy?.pricingStrategySequence ?? [])
    .map((s) => `${s.pctTime}% → ${s.postureName ?? POSTURE_NAMES[s.posture] ?? s.posture}`).join(' · ');
  $: progressPct = parent && parent.targetQty > 0 ? Math.min(100, (parent.filledQty / parent.targetQty) * 100)
    : parent && parent.targetAmount > 0 ? Math.min(100, (parent.filledAmount / parent.targetAmount) * 100) : null;

  const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
</script>

<div class="tr" class:tr-paper={paperVenue} class:tr-live={parent && !paperVenue}>
  {#if !parent && loading}
    <div class="tr-skeleton" aria-busy="true">Loading report…</div>
  {:else if !parent && error}
    <p class="tr-error" role="alert">{error}</p>
  {:else if parent}
    <header class="tr-head">
      <div class="tr-title">
        <span class={`tr-side tr-side-${String(parent.side).toLowerCase()}`}>{parent.side}</span>
        <strong class="tr-symbol">{parent.symbol}</strong>
        <span class="tr-qty">{fmtQty(parent.targetQty ?? parent.filledQty)}{#if parent.targetAmount} ({formatQuoteAmount(parent.targetAmount, currency)}){/if}</span>
        <span class={`tr-status tr-status-${statusTone(parent.status)}`}>{STATUS_LABELS[parent.status] ?? parent.status}{#if working && detail} · live{/if}</span>
        {#if parent.cancelRequestedAt}<span class="tr-status tr-status-cancelled">cancel requested</span>{/if}
      </div>
      <div class="tr-badges">
        <span class={`tr-mode ${paperVenue ? 'paper' : 'live'}`}>{modeBadge}</span>
        <span class="tr-badge" title={scorecard?.feeRateSource ?? parent.feeRateSource ?? ''}>
          {fee.paper ? '⚠ ' : ''}{fee.label}
          {#if typeof scorecard?.makerFeeRate === 'number'} · maker {(scorecard.makerFeeRate * 100).toFixed(2)} % / taker {(scorecard.takerFeeRate * 100).toFixed(2)} %{/if}
        </span>
        <span class="tr-badge">{parent.source}{#if parent.strategyId} · {parent.strategyId}{/if}</span>
      </div>
      <div class="tr-meta">
        <span>Started {when(parent.startedAt)}</span>
        <span>{parent.endedAt ? `Ended ${when(parent.endedAt)}` : `End by ${when(parent.endBy)}`}</span>
        {#if policyText}<span>{policyText}</span>{/if}
        {#if typeof parent.cyclesCompleted === 'number'}<span>{parent.cyclesCompleted}{parent.cyclesPlanned ? ` / ${parent.cyclesPlanned}` : ''} cycles</span>{/if}
      </div>
      {#if fee.paper}
        <p class="tr-paper-note" role="note">Paper fee schedule in effect — fee savings below are simulated at the paper rates, not live venue fees.</p>
      {/if}
      {#if working}
        <div class="tr-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={progressPct ?? 0}
          aria-label={`Filled ${fmtQty(parent.filledQty)} of ${fmtQty(parent.targetQty)}`}>
          <span class="tr-progress-fill" style={`width: ${progressPct ?? 0}%`}></span>
        </div>
        <div class="tr-progress-text">{fmtQty(parent.filledQty)} / {fmtQty(parent.targetQty)} filled{#if progressPct !== null} · {progressPct.toFixed(0)} %{/if}{#if !detail} · loading detail…{/if}</div>
      {/if}
      {#if error}<p class="tr-error" role="alert">{error}</p>{/if}
    </header>

    <section class="tr-tiles" aria-label="Scorecard">
      {#each tiles as t (t.key)}
        <div class={`tr-tile tone-${t.tone}`} title={t.hint ?? ''}>
          <span class="tr-tile-label">{t.label}</span>
          <span class="tr-tile-value">{t.text}</span>
          {#if t.sub}<span class="tr-tile-sub">{t.sub}</span>{/if}
        </div>
      {/each}
    </section>

    <NarrativeStrip text={story} />

    <!-- PR-C2: execution-quality tiles — patience premium, pace adherence, 5-min reversion (server-stamped), slice latency. -->
    <section class="tr-tiles tr-quality" aria-label="Execution quality">
      {#each quality as t (t.key)}
        <div class={`tr-tile tone-${t.tone}`} title={t.hint ?? ''}>
          <span class="tr-tile-label">{t.label}</span>
          <span class="tr-tile-value">{t.text}</span>
          {#if t.sub}<span class="tr-tile-sub">{t.sub}</span>{/if}
        </div>
      {/each}
      <LatencyTile p50Ms={scorecard?.sliceLatencyP50Ms ?? null} p95Ms={scorecard?.sliceLatencyP95Ms ?? null} loading={!detail && loading} />
    </section>

    <section class="tr-section">
      <h3>Savings waterfall</h3>
      <SavingsWaterfall {waterfall} {currency} />
    </section>
    <section class="tr-section">
      <h3>Execution tape</h3>
      <ExecutionTape series={tape} {currency} />
    </section>
    <section class="tr-section">
      <h3>Pace</h3>
      <PaceChart series={pace} />
    </section>
    <section class="tr-section">
      <h3>Slices <span class="tr-count">({children.length})</span></h3>
      {#if blocked}
        <p class="tr-blocked" role="status">{blocked.text} — retrying each cycle until the deadline.</p>
      {/if}
      <ChildBlotter {children} {parent} />
    </section>
    <section class="tr-section">
      <h3>Market footprint</h3>
      <MarketFootprint {scorecard} {parent} />
    </section>
    <section class="tr-section">
      <MakerCaptureHeatmap grid={heatmap} loading={heatmapLoading} error={heatmapError}
        title={`Maker capture by day × hour (UTC) · ${parent.source === 'manual' ? 'all manual parents' : 'this strategy'}`}
        emptyText="No fills to map yet." />
      {#if heatmapError}<button type="button" class="btn btn-ghost btn-sm" on:click={() => loadHeatmap(parentId)}>Retry</button>{/if}
    </section>
    {#if ladderText}
      <p class="tr-ladder"><span class="tr-tile-label">Posture ladder</span> {ladderText}</p>
    {/if}
  {/if}
</div>

<style>
  .tr { display: flex; flex-direction: column; gap: var(--spacing-md, 0.75rem); min-width: 0; font-size: var(--font-size-sm, 0.85rem); }
  .tr-head { display: flex; flex-direction: column; gap: 0.4rem; }
  .tr-title { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; font-size: var(--font-size-lg, 1.1rem); }
  .tr-side { font-weight: 700; text-transform: uppercase; font-size: var(--font-size-sm, 0.85rem); padding: 0.1rem 0.5rem; border-radius: 4px; }
  .tr-side-buy { color: var(--color-success, #00ff88); background: var(--color-success-alpha-20, rgba(0, 255, 136, 0.2)); }
  .tr-side-sell { color: var(--color-danger, #ff3b3b); background: var(--color-danger-alpha-20, rgba(255, 59, 59, 0.2)); }
  .tr-symbol { color: var(--text-primary, #fff); }
  .tr-qty { font-variant-numeric: tabular-nums; color: var(--text-secondary, #8a8b8d); }
  .tr-status { font-size: var(--font-size-xs, 0.7rem); letter-spacing: 0.06em; padding: 0.1rem 0.5rem; border-radius: 999px; border: 1px solid var(--border-secondary, #2a2b2e); text-transform: uppercase; }
  .tr-status-working { color: var(--color-warning, #ffaa00); border-color: var(--color-warning, #ffaa00); }
  .tr-status-done { color: var(--color-success, #00ff88); border-color: var(--color-success, #00ff88); }
  .tr-status-partial { color: var(--color-primary, #00d4ff); border-color: var(--color-primary, #00d4ff); }
  .tr-status-cancelled { color: var(--text-secondary, #8a8b8d); }
  .tr-status-failed { color: var(--color-danger, #ff3b3b); border-color: var(--color-danger, #ff3b3b); }
  .tr-badges { display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center; }
  .tr-badge { font-size: var(--font-size-xs, 0.7rem); padding: 0.1rem 0.5rem; border-radius: 999px; border: 1px solid var(--border-secondary, #2a2b2e); color: var(--text-secondary, #8a8b8d); }
  .tr-mode { font-size: var(--font-size-xs, 0.7rem); letter-spacing: 0.08em; font-weight: 700; padding: 0.1rem 0.5rem; border-radius: 999px; }
  .tr-mode.paper { color: var(--at-banner-paper-text, #9db8f0); background: var(--at-banner-paper-bg, rgba(91, 141, 239, 0.12)); border: 1px solid var(--at-accent-paper, #5b8def); }
  .tr-mode.live { color: var(--at-banner-live-text, #ffd7c2); background: var(--at-banner-live-bg, #2a0f0a); border: 1px solid var(--at-accent-live, #ff7a45); }
  .tr-live .tr-head { border-left: 3px solid var(--at-accent-live, #ff7a45); padding-left: 0.6rem; }
  .tr-paper .tr-head { border-left: 3px solid var(--at-accent-paper, #5b8def); padding-left: 0.6rem; }
  .tr-meta { display: flex; flex-wrap: wrap; gap: 0.35rem 1rem; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); font-variant-numeric: tabular-nums; }
  .tr-paper-note { margin: 0; font-size: var(--font-size-xs, 0.7rem); color: var(--at-banner-paper-text, #9db8f0); }
  .tr-progress { height: 6px; background: var(--bg-quaternary, #212226); border-radius: 999px; overflow: hidden; }
  .tr-progress-fill { display: block; height: 100%; background: var(--color-primary, #00d4ff); transition: width var(--transition-base, 200ms ease); }
  @media (prefers-reduced-motion: reduce) { .tr-progress-fill { transition: none; } }
  .tr-progress-text { font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); font-variant-numeric: tabular-nums; }
  .tr-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--spacing-sm, 0.5rem); }
  .tr-tile { background: var(--bg-secondary, #111214); border: 1px solid var(--border-primary, #1e1f22); border-radius: var(--radius-base, 0.5rem); padding: 0.55rem 0.7rem; display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
  .tr-tile-label { font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); text-transform: uppercase; letter-spacing: 0.04em; }
  .tr-tile-value { font-size: var(--font-size-xl, 1.3rem); font-weight: var(--font-weight-semibold, 600); font-variant-numeric: tabular-nums; color: var(--text-primary, #fff); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .tr-tile-sub { font-size: var(--font-size-xs, 0.7rem); color: var(--text-muted, #6a6b6d); font-variant-numeric: tabular-nums; }
  .tone-gain .tr-tile-value { color: var(--color-success, #00ff88); }
  .tone-loss .tr-tile-value { color: var(--color-danger, #ff3b3b); }
  .tr-quality { grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); }
  .tr-section h3 { margin: 0 0 0.35rem; font-size: var(--font-size-sm, 0.85rem); text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-secondary, #8a8b8d); }
  .tr-count { color: var(--text-muted, #6a6b6d); text-transform: none; }
  .tr-blocked {
    margin: 0 0 8px;
    padding: 6px 10px;
    border-left: 3px solid var(--color-danger, #ff4d4f);
    background: color-mix(in srgb, var(--color-danger, #ff4d4f) 8%, transparent);
    color: var(--color-danger, #ff4d4f);
    font-size: 0.85rem;
  }
  .tr-ladder { margin: 0; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); }
  .tr-skeleton { color: var(--text-muted, #6a6b6d); padding: 1rem 0; }
  .tr-error { margin: 0; color: var(--color-danger, #ff3b3b); }
</style>
